import type { AnnotationsPluginMessage } from '@st/document-viewer-kit/plugins/annotations'
import { Task } from '@st/redux'
import type { Contract, Signer } from '@st/sdk'
import { Effect } from 'effect'
import { match } from '@st/pattern'
import { create } from 'mutative'
import { deepEqual } from '@st/util/deep-equal'
import { IdGenerator } from '~/effects'
import {
  contractDraftFields,
  removeSignerFields,
  placeSignerField,
  type ContractViewer
} from './fields'

/** Signer edits and their nested interaction share one contract-scoped lifetime. */
export type ContractEditorState = {
  contractId: string
  // Null follows saved signers until the first edit; fields remain in the viewer overlay.
  signers: Signer[] | null
  interaction:
    | { type: 'editing' }
    | { type: 'addingSigner'; role: string }
    | { type: 'confirmingRemoval'; signerId: string }
}

export type ContractEditorMessage =
  | { type: 'signerRenamed'; signerId: string; displayName: string }
  | { type: 'signerAddRequested'; role: string }
  | { type: 'signerAdded'; signerId: string }
  | { type: 'signerRemoveRequested'; signerId: string }
  | { type: 'signerRemoveCancelled' }
  | { type: 'signerRemoveConfirmed' }
  | { type: 'fieldPlacementRequested'; signerId: string; fieldType: 'signature' | 'date' }

/** The host decides which roles may be added and which existing signers may be removed. */
export type ContractSignerPolicy = {
  addableRoles: string[]
  removableSignerIds: string[]
}

type ContractEditorUpdate = {
  state: ContractEditorState
  viewerMessages: AnnotationsPluginMessage[]
}

export function initContractEditor(contractId: string): ContractEditorState {
  return { contractId, signers: null, interaction: { type: 'editing' } }
}

export function contractEditorSigners(state: ContractEditorState, savedSigners: Signer[]) {
  return state.signers ?? savedSigners
}

/** A new contract resets the editor; acknowledged signer edits resume following saved data. */
export function reconcileContractEditor(
  state: ContractEditorState,
  contract: Contract
): ContractEditorState {
  if (state.contractId != contract.id) return initContractEditor(contract.id!)
  if (state.signers && deepEqual(state.signers, contract.signers))
    return { ...state, signers: null }
  return state
}

/** The host supplies role policy; this editor owns identity, confirmation, and field coordination. */
export function updateContractEditor(
  state: ContractEditorState,
  contract: Contract,
  viewer: ContractViewer,
  message: ContractEditorMessage,
  policy: ContractSignerPolicy
): ContractEditorUpdate {
  const signers = contractEditorSigners(state, contract.signers)
  const updated = (state: ContractEditorState): ContractEditorUpdate => ({
    state,
    viewerMessages: []
  })
  return match({ interaction: state.interaction, message })
    .returnType<ContractEditorUpdate>()
    .with(
      { interaction: { type: 'editing' }, message: { type: 'signerRenamed' } },
      ({ message: { signerId, displayName } }) =>
        updated({
          ...state,
          signers: create(signers, (draft) => {
            draft.find((signer) => signer.id == signerId)!.displayName = displayName
          })
        })
    )
    .with(
      { interaction: { type: 'editing' }, message: { type: 'signerAddRequested' } },
      ({ message: { role } }) => {
        if (!policy.addableRoles.includes(role)) throw new Error('Cannot add this signer role')
        return updated({ ...state, interaction: { type: 'addingSigner', role } })
      }
    )
    .with(
      { interaction: { type: 'addingSigner' }, message: { type: 'signerAdded' } },
      ({ interaction, message }) =>
        updated({
          ...state,
          signers: [...signers, { id: message.signerId, role: interaction.role, displayName: '' }],
          interaction: { type: 'editing' }
        })
    )
    .with(
      { interaction: { type: 'editing' }, message: { type: 'signerRemoveRequested' } },
      ({ message: { signerId } }) => {
        if (!policy.removableSignerIds.includes(signerId))
          throw new Error('Cannot remove this signer')
        return updated({ ...state, interaction: { type: 'confirmingRemoval', signerId } })
      }
    )
    .with(
      { interaction: { type: 'confirmingRemoval' }, message: { type: 'signerRemoveCancelled' } },
      () => updated({ ...state, interaction: { type: 'editing' } })
    )
    .with(
      { interaction: { type: 'confirmingRemoval' }, message: { type: 'signerRemoveConfirmed' } },
      ({ interaction }) => ({
        state: {
          ...state,
          signers: signers.filter((signer) => signer.id != interaction.signerId),
          interaction: { type: 'editing' }
        },
        viewerMessages: removeSignerFields(viewer, interaction.signerId)
      })
    )
    .with(
      { interaction: { type: 'editing' }, message: { type: 'fieldPlacementRequested' } },
      ({ message: { signerId, fieldType } }) => ({
        state,
        viewerMessages: placeSignerField(
          signers.find((signer) => signer.id == signerId)!,
          fieldType
        )
      })
    )
    .otherwise(() => {
      throw new Error(
        `Unexpected contract editor action ${message.type} in ${state.interaction.type}`
      )
    })
}

// The contract ID selects the Task session; ID generation itself is independent of the contract.
const generateSignerId = Task.define((_contractId: string) =>
  Effect.gen(function* () {
    const generator = yield* IdGenerator
    const signerId = yield* generator.generate
    return { type: 'signerAdded', signerId } satisfies ContractEditorMessage
  })
)

/** Leaving Signing or cancelling its editor revokes the local ID task and its output. */
export function contractEditorTasks(state: ContractEditorState) {
  return match(state.interaction)
    .with({ type: 'addingSigner' }, () => [generateSignerId(state.contractId)])
    .otherwise(() => [])
}

// Publication requires a name and a signature box for every signer.
export function signingComplete(contract: Contract): boolean {
  return (
    contract.signers.length > 0 &&
    contract.signers.every(
      (signer) =>
        (signer.displayName ?? '').trim() != '' &&
        contract.fields.some((field) => field.signerId == signer.id && field.type == 'signature')
    )
  )
}

/** The one complete definition used for dirty comparison and atomic persistence. */
export function contractEditorDefinition(
  state: ContractEditorState,
  contract: Contract,
  viewer: ContractViewer
): Pick<Contract, 'signers' | 'fields'> {
  return {
    signers: contractEditorSigners(state, contract.signers),
    fields: contractDraftFields(contract, viewer)
  }
}

/** Saving waits for pending signer-ID generation or removal confirmation to finish. */
export function contractEditorIdle(state: ContractEditorState): boolean {
  return state.interaction.type == 'editing'
}

/** Compare the complete editable definition, including the viewer's unsaved field overlay. */
export function contractEditorDirty(
  state: ContractEditorState,
  contract: Contract,
  viewer: ContractViewer
): boolean {
  return !deepEqual(contractEditorDefinition(state, contract, viewer), {
    signers: contract.signers,
    fields: contract.fields
  })
}
